export class ApiUnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'ApiUnauthorizedError';
  }
}

export class ApiHttpError extends Error {
  status: number;
  code?: string;
  traceId?: string;
  url?: string;
  method?: string;

  constructor(
    status: number,
    message?: string,
    code?: string,
    traceId?: string,
    url?: string,
    method?: string,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.code = code;
    this.traceId = traceId;
    this.url = url;
    this.method = method;
  }
}

const HTTP_STATUS_TEXT: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  413: 'Payload Too Large',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

export interface FormattedApiError {
  title: string;
  description?: string;
}

type ApiErrorLike = Error & {
  status: number;
  code?: string;
  traceId?: string;
  url?: string;
  method?: string;
};

function isApiErrorLike(err: unknown): err is ApiErrorLike {
  return (
    err instanceof Error &&
    'status' in err &&
    typeof (err as { status: unknown }).status === 'number'
  );
}

/**
 * Formats any thrown value into a structured { title, description } suitable
 * for display in a toast. For ApiHttpError, includes the HTTP method, path,
 * status, server message, and trace ID.
 */
export function formatApiError(
  err: unknown,
  fallback = 'An unexpected error occurred',
): FormattedApiError {
  if (err instanceof ApiHttpError || isApiErrorLike(err)) {
    const e = err as ApiErrorLike;
    const isNetwork = e.status === 0;
    const statusText = isNetwork
      ? 'Network Error'
      : (HTTP_STATUS_TEXT[e.status] ?? `HTTP ${e.status}`);
    const statusLabel = isNetwork ? 'Network Error' : `${e.status} ${statusText}`;
    const path = e.url?.replace(/^\/api\/v\d+/, '');
    const location = [e.method?.toUpperCase(), path].filter(Boolean).join(' ');
    const title = location ? `${location} — ${statusLabel}` : statusLabel;

    const parts: string[] = [];
    if (e.message && e.message !== `HTTP ${e.status}`) parts.push(e.message);
    if (e.code) parts.push(`code: ${e.code}`);
    if (e.traceId) parts.push(`trace: ${e.traceId}`);

    return { title, description: parts.length > 0 ? parts.join(' · ') : undefined };
  }

  if (err instanceof Error) return { title: err.message };
  return { title: fallback };
}

/**
 * Returns a human-readable message from any thrown value.
 * Handles ApiHttpError (code + message), ApiUnauthorizedError, generic Error,
 * and RFC 9457 { detail } / { title } objects that may arrive from Go services.
 */
export function extractErrorMessage(
  err: unknown,
  fallback = 'An unexpected error occurred',
): string {
  if (err instanceof ApiHttpError) return err.message;
  if (err instanceof Error) return err.message;
  if (err != null && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    if (typeof e.detail === 'string') return e.detail;
    if (typeof e.message === 'string') return e.message;
  }
  return fallback;
}
